import { Component, HostBinding, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserPreferencesDispatch } from '@state/user-preferences/user-preferences.dispatch';
import { UserPreferencesState } from '@state/user-preferences/user-preferences.state';
import { debounce } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { MenuLink, VM_SideNavSelector } from './side-nav.selector';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { TestHooks } from './side-nav.hooks';

@UntilDestroy()
@Component({
	selector: 'app-side-nav',
	templateUrl: './side-nav.component.html',
	styleUrls: ['./side-nav.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class SideNavComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public selectedGroups: { [key: string]: boolean } = {};
	public allGroupsExpanded = false;
	public sideNavCollapsed = true; // used to manage sidenav current state
	public matterFileTypes = MatterFileType;
	public previousUrl;
	public currentUrl;
	public backString;
	public showBackButton = false;
	public isActiveClass = 'is-active';

	@HostBinding('class.fileType-sale') get saleFileType() {
		return this.fileType === MatterFileType.SALE;
	}

	@HostBinding('class.fileType-purchase') get purchaseFileType() {
		return this.fileType === MatterFileType.PURCHASE;
	}

	@HostBinding('class.fileType-refinance') get refinanceFileType() {
		return this.fileType === MatterFileType.REFINANCE;
	}

	@HostBinding('class.compact') get compact() {
		return this.sideNavCompact;
	}

	@ViewSelectSnapshot(UserPreferencesState.getSideNavCompact)
	sideNavCompact: boolean;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(VM_SideNavSelector.MenuLinks) menuLinks$: Observable<MenuLink[]>;
	@Select(VM_SideNavSelector.SelectedGroups) selectedGroups$: Observable<{
		[key: string]: boolean;
	}>;

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private navService: NavigationService,
		private userPreferencesDispatch: UserPreferencesDispatch,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.selectedGroups$
			.pipe(untilDestroyed(this))
			.subscribe((updatedGroups) => {
				this.selectedGroups = { ...updatedGroups };
				this.updateAllGroupsExpanded();
			});

		this.sideNavCollapsed = this.sideNavCompact;
		this.router.events
			.pipe(
				untilDestroyed(this),
				filter((e) => e instanceof NavigationEnd),
				map(() => this.activatedRoute),
			)
			.subscribe((res) => {
				if (this.currentUrl) {
					this.previousUrl = this.currentUrl;
				}
				this.backString = res?.snapshot?.queryParams['back-string'] ?? null;
				this.showBackButton = this.backString && this.previousUrl;
				this.currentUrl = res?.snapshot?.['_routerState']?.url;
			});
	}

	goToPreviousPage() {
		this.navService.goToUrl(this.previousUrl);
	}

	toggleSelectedGroup(group: number) {
		const updatedGroups = { ...this.selectedGroups };
		updatedGroups[group] = !updatedGroups[group];
		this.userPreferencesDispatch.SetDocumentNavigationState(
			this.matterId,
			updatedGroups,
		);
	}

	toggleAllSelectedGroups() {
		const openAll = this.allGroupsExpanded;
		const updatedGroups = { ...this.selectedGroups };
		for (const key in updatedGroups) {
			updatedGroups[key] = !openAll;
		}
		this.userPreferencesDispatch.SetDocumentNavigationState(
			this.matterId,
			updatedGroups,
		);
	}

	updateAllGroupsExpanded() {
		this.allGroupsExpanded = Object.values(this.selectedGroups).every(
			(val) => val,
		);
	}

	sideNavHoverEnter = debounce(() => {
		if (this.sideNavCompact) {
			this.sideNavCollapsed = false;
		}
	}, 300);

	sideNavHoverLeave() {
		this.sideNavHoverEnter.cancel();
		if (this.sideNavCompact) {
			this.sideNavCollapsed = true;
		}
	}

	trackGroup(index: number, item: MenuLink) {
		return item.id;
	}

	toggleSideNav() {
		if (!this.sideNavCollapsed) {
			this.analytics.logEvent(AnalyticsEvent.SIDE_NAV_IN_FILE_COLLAPSE_CLICK);
		}
		this.userPreferencesDispatch.ToggleSideNavState();
		this.sideNavCollapsed = !this.sideNavCollapsed;
	}
}
